import { z } from 'zod';

import type { ContactDto, IdAccountDto, IdContactDto } from '@/services/ws-crm/src/main/resources/rest-api';
import type {
  IdTaskDto,
  TaskDto,
  IdSalesPromotionDto,
  IdTaskTopicDto,
  OutcomeFilter,
} from '@/services/ws-funnel/src/main/resources/rest-api';
import type { IdLabelDto } from '@/services/ws-labels/src/main/resources/rest-api';
import type { ModalState } from '@/js/libs/hooks/useModalState';
import type { SelectionState } from '@/js/libs/hooks/useSelectionState';

import type { ColumnComponentPropsV2, ColumnV2, Option, RecursiveKeyOf } from 'src/types.ts';

const TaskTableStatusZod = z.enum(['ALL', 'UNASSIGNED', 'IN_PROGRESS', 'SNOOZED', 'DONE', 'ARCHIVED']);
export const TaskTableStatus = TaskTableStatusZod.enum;
export type TaskTableStatus = (typeof TaskTableStatus)[keyof typeof TaskTableStatus];

const TaskTableSearchKeyZod = z.enum(['STATUS']);
export const TaskTableSearchKey = TaskTableSearchKeyZod.enum;
export type TaskTableSearchKey = (typeof TaskTableSearchKey)[keyof typeof TaskTableSearchKey];

const InvoicingAccountKey = 'invoicingAccount' as const satisfies keyof FiltersState;
export const SalesContactKey = 'assignedContact' as const satisfies keyof FiltersState;

export const TaskTableSearchParams = z.object({
  [TaskTableSearchKey.STATUS]: TaskTableStatusZod.optional(),
  [InvoicingAccountKey]: z.string().optional(),
  [SalesContactKey]: z.string().optional(),
});
export type TaskTableSearchParams = z.infer<typeof TaskTableSearchParams>;

export type ColumnName = RecursiveKeyOf<TaskDto> | 'selection' | 'actions';

export interface CustomColumnProps {
  item: TaskDto;
  selection?: SelectionState<TaskDto, IdTaskDto>;
  selectionDisabled?: boolean;
  assignModalState?: ModalState<IdTaskDto[]>;
  archiveModalState?: ModalState<IdTaskDto[]>;
  restoreModalState?: ModalState<IdTaskDto>;
}

export interface ColumnProps extends ColumnComponentPropsV2<ColumnName>, CustomColumnProps {}

export type Column = ColumnV2<ColumnName, ColumnProps>;

export enum TimeFrame {
  Last7Days = 'LAST_7_DAYS',
  CurrentMonth = 'CURRENT_MONTH',
  CurrentYear = 'CURRENT_YEAR',
}

export type FiltersState = {
  search: string;
  topic: Option<IdTaskTopicDto> | null;
  outcome: Option<OutcomeFilter> | null;
  timeFrame: Option<TimeFrame> | null;
  label: Option<IdLabelDto> | null;
  source: Option<string> | null;
  sales: Option<ContactDto> | null;
  assignedContact: Option<IdContactDto> | null;
  invoicingAccount: Option<IdAccountDto> | null;
  promotion: Option<IdSalesPromotionDto> | null;
};
